import React, { useEffect, useMemo } from "react";

import { EntityType, RelatedAs } from "../../../api/stakeholder_relationships";
import { useAppSelector } from "../../../app/store";
import { AutoSelectVerticalNav } from "../../../components/ui/navigation/AutoSelectVerticalNav";
import { TabItem } from "../../../components/ui/navigation/VerticalNavPanel";
import { getUniqueStakeholdersByEntity } from "../../stakeholder/logic/stakeholderSlice";
import { getCaseAccounts } from "../../accounts/logic/slice";

interface AccountStakeholderNavProps {
  selectedAccountNumber?: string;
  selectedStakeholderId?: string;
  onSelectionChange: (accountNumber: string, stakeholderId: string) => void;
}

const NAV_STAKEHOLDER_ROLES: RelatedAs[] = [
  RelatedAs.PRIMARY_ACCOUNT_HOLDER,
  RelatedAs.JOINT_ACCOUNT_HOLDER,
  RelatedAs.BENEFICIARY_ACCOUNT_HOLDER,
  RelatedAs.NOMINEE,
  RelatedAs.MANDATE_HOLDER,
  RelatedAs.POWER_OF_ATTORNEY_HOLDER,
  RelatedAs.EMPLOYEE,
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.SALES_MANAGER,
  RelatedAs.BRANCH_MANAGER,
  RelatedAs.KYC_OFFICER,
  RelatedAs.CUSTOMER_SERVICE_OFFICER,
  RelatedAs.TELLER,
  RelatedAs.BUSINESS_CORRESPONDENT,
  RelatedAs.BUSINESS_CORRESPONDENT_AGENT,
  RelatedAs.VENDOR
];

const buildNavId = (accountNumber: string, stakeholderId: string) =>
  `${accountNumber}::${stakeholderId}`;

const AccountStakeholderNav: React.FC<AccountStakeholderNavProps> = ({
  selectedAccountNumber,
  selectedStakeholderId,
  onSelectionChange,
}) => {
  const accounts = useAppSelector(getCaseAccounts);

  const stakeholdersByAccount = useAppSelector((state) =>
    accounts.map((account) => ({
      account,
      stakeholders: getUniqueStakeholdersByEntity(
        state,
        EntityType.ACCOUNT,
        account.account_number,
        NAV_STAKEHOLDER_ROLES,
      ),
    })),
  );

  const items: TabItem[] = useMemo(
    () =>
      stakeholdersByAccount.map(({ account, stakeholders }) => ({
        id: account.account_number,
        header: account.account_name,
        subheader: account.ext_account_number,
        children: stakeholders.map((stakeholder) => ({
          id: buildNavId(account.account_number, stakeholder.stakeholder_id),
          header: stakeholder.name,
        })),
      })),
    [stakeholdersByAccount],
  );

  const selectedId = selectedAccountNumber && selectedStakeholderId
      ? buildNavId(selectedAccountNumber, selectedStakeholderId)
      : undefined;

  useEffect(() => {
    if (selectedId) return;

    const firstAccountWithPeople = items.find(
      (item) => (item.children?.length ?? 0) > 0,
    );
    const firstStakeholder = firstAccountWithPeople?.children?.[0];
    if (!firstStakeholder) return;

    const [loanReferenceId, stakeholderId] = firstStakeholder.id.split("::");
    onSelectionChange(loanReferenceId, stakeholderId);
  }, [items, selectedId, onSelectionChange]);

  const handleSelectionChange = (id: string) => {
    const [loanReferenceId, stakeholderId] = id.split("::");
    if (!stakeholderId) return;

    onSelectionChange(loanReferenceId, stakeholderId);
  };

  return (
    <AutoSelectVerticalNav
      title="Accounts"
      items={items}
      selectedId={selectedId}
      onSelectionChange={handleSelectionChange}
      className="w-[260px] shrink-0"
    />
  );
};

export default AccountStakeholderNav;
